import useStyles from "../styles";
import {
  Grid,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { useEffect, useState } from "react";
import InputGroup from "../../../components/FormComponent/InputGroup";
import Button from "../../../components/Button";
import { toast } from "react-toastify";
import NoData from "../../dashboard/components/NotFoundCard/NoData";
import { httprequest } from "../../../data/api";
import NoAccount from "../../../assets/images/emptyacct.png";
import { Icon } from "@iconify/react/dist/iconify.js";
import Modal from "../../../components/Modal";
import Select from "react-select";
import currencyCodes from "currency-codes";
import getSymbolFromCurrency from "currency-symbol-map";

const PROFILE_ENDPOINT = "api/v1/profile";
const VERIFY_OTP_ENDPOINT = "api/v1/profile/verify-otp";
const RESEND_OTP_ENDPOINT = "api/v1/profile/resend-otp";

const BankingInfo = ({ data }) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [addAccountModal, setAddAccountModal] = useState(false);
  const [editAccountModal, setEditAccountModal] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [selectedBankIndex, setSelectedBankIndex] = useState(null);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [otp, setOtp] = useState("");
  const [otpModal, setOtpModal] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [currentBankId, setCurrentBankId] = useState("");
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const bankAccounts = data?.bank_details || [];

  const [bankDetails, setBankDetails] = useState({
    bank_name: "",
    account_number: "",
    account_name: "",
    currency: "",
    routing_number: "",
    sort_code: "",
    status: "active",
    primary: false
  });

  const handleAccordionChange = (index) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? index : false);
  };

  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return "N/A";
    const symbol = getSymbolFromCurrency(currencyCode);

    return symbol || currencyCode;
  };

  useEffect(() => {
    const loadCurrencyOptions = () => {
      try {
        const currencies = currencyCodes.data.map(currency => ({
          value: currency.code,
          label: `${currency.currency} (${currency.code})`
        }));

        currencies.sort((a, b) => a.label.localeCompare(b.label));

        setCurrencyOptions(currencies);
      } catch (error) {
        setCurrencyOptions([
          { value: "USD", label: "US Dollar (USD)" },
          { value: "EUR", label: "Euro (EUR)" },
          { value: "GBP", label: "British Pound (GBP)" },
          { value: "NGN", label: "Nigerian Naira (NGN)" },
          { value: "CAD", label: "Canadian Dollar (CAD)" },
          { value: "AUD", label: "Australian Dollar (AUD)" }
        ]);
      }
    };

    loadCurrencyOptions();
  }, []);

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleCurrencyChange = (selectedOption) => {
    setBankDetails(prev => ({
      ...prev,
      currency: selectedOption ? selectedOption.value : ""
    }));
  };

  const getSelectedCurrency = () => {
    return currencyOptions.find(option => option.value === bankDetails.currency) || null;
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setBankDetails(prev => ({ ...prev, [name]: checked }));
  };

  const handleStatusChange = (e) => {
    const checked = e.target.checked;
    setBankDetails(prev => ({ ...prev, status: checked ? "active" : "inactive" }));
  };

  const resetBankDetails = () => {
    setBankDetails({
      bank_name: "",
      account_number: "",
      account_name: "",
      currency: "",
      routing_number: "",
      sort_code: "",
      status: "active",
      primary: false
    });
    setCurrentBankId("");
  };

  const handleAddAccountModal = () => {
    setAddAccountModal(!addAccountModal);
    if (addAccountModal) {
      resetBankDetails();
    }
  };

  const handleOtpModal = () => {
    setOtpModal(!otpModal);
  };

  const resendOtp = async () => {
    setIsOtpLoading(true);
    try {
      const response = await httprequest.post(RESEND_OTP_ENDPOINT, {
        action: "resend_otp"
      });
      toast.success(response?.data?.message || "OTP resent successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    setIsOtpLoading(true);
    try {
      const response = await httprequest.post(VERIFY_OTP_ENDPOINT, {
        otp
      });
      toast.success(response?.data?.message || "OTP resent successfully");
      handleOtpModal();
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleEditAccountModal = (index = null) => {
    if (index !== null) {
      setSelectedBankIndex(index);
      const bankAccount = bankAccounts[index];

      setCurrentBankId(bankAccount?.id || bankAccount?._id || "");

      setBankDetails({
        id: bankAccount?.id,
        bank_name: bankAccount?.bank_name || "",
        account_number: bankAccount?.account_number || "",
        account_name: bankAccount?.account_name || "",
        currency: bankAccount?.currency || "",
        routing_number: bankAccount?.routing_number || "",
        sort_code: bankAccount?.sort_code || "",
        status: bankAccount?.status || "active",
        primary: bankAccount?.primary || false
      });
    } else {
      resetBankDetails();
    }
    setEditAccountModal(!editAccountModal);
  };

  const handleDeleteAccountModal = (index = null) => {
    if (index !== null) {
      setSelectedBankIndex(index);
      const bankAccount = bankAccounts[index];

      setCurrentBankId(bankAccount?.id || bankAccount?._id || "");
    }
    setDeleteAccountModal(!deleteAccountModal);
  };

  const validateBankDetails = () => {
    const requiredFields = ["bank_name", "account_number", "account_name", "currency"];
    const missingFields = requiredFields.filter(field => !bankDetails[field]?.trim());

    if (missingFields.length > 0) {
      toast.error("Please fill in all required fields");

      return false;
    }

    if (!/^\d+$/.test(bankDetails.account_number)) {
      toast.error("Account number must contain only numbers");

      return false;
    }

    return true;
  };

  const handleBankAction = async (action) => {
    if (action !== "delete" && !validateBankDetails()) return;

    setIsLoading(true);

    let payload = {};
    let successMessage = "";
    let errorMessage = "";

    try {
      const bankDetailsPayload = {
        bank_name: bankDetails.bank_name,
        account_number: bankDetails.account_number,
        account_name: bankDetails.account_name,
        currency: bankDetails.currency,
        status: bankDetails.status,
        primary: bankDetails.primary
      };

      if (bankDetails.routing_number && bankDetails.routing_number.trim() !== "") {
        bankDetailsPayload.routing_number = bankDetails.routing_number;
      }

      if (bankDetails.sort_code && bankDetails.sort_code.trim() !== "") {
        bankDetailsPayload.sort_code = bankDetails.sort_code;
      }

      switch (action) {
      case "add":
        payload = {
          bank_details: [{
            ...bankDetailsPayload,
            action: "add"
          }]
        };
        successMessage = "Bank account added successfully";
        errorMessage = "Failed to add bank account";
        break;

      case "edit":
        payload = {
          bank_details: [{
            ...bankDetailsPayload,
            action: "edit",
            id: currentBankId
          }]
        };
        successMessage = "Bank account updated successfully";
        errorMessage = "Failed to update bank account";
        break;

      case "delete":
        payload = {
          bank_details: [{
            action: "delete",
            id: currentBankId
          }]
        };
        successMessage = "Bank account deleted successfully";
        errorMessage = "Failed to delete bank account";
        break;

      default:
        throw new Error("Invalid action");
      }

      const response = await httprequest.patch(PROFILE_ENDPOINT, payload);
      toast.success(response?.data?.message || successMessage);

      switch (action) {
      case "add":
        handleAddAccountModal();
        break;
      case "edit":
        handleEditAccountModal();
        break;
      case "delete":
        handleDeleteAccountModal();
        break;
      default:
        break;
      }

      resetBankDetails();

      handleOtpModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrencyName = (currencyCode) => {
    if (!currencyCode) return "N/A";

    const currency = currencyOptions.find(option => option.value === currencyCode);
    if (currency) {
      return currency.label.split(" (")[0];
    }

    const commonCurrencies = {
      "USD": "US Dollar",
      "EUR": "Euro",
      "GBP": "British Pound",
      "NGN": "Nigerian Naira",
      "CAD": "Canadian Dollar",
      "AUD": "Australian Dollar"
    };

    return commonCurrencies[currencyCode] || currencyCode;
  };

  useEffect(() => {
    if (!addAccountModal && !editAccountModal) {
      resetBankDetails();
    }
  }, [addAccountModal, editAccountModal]);

  return (
    <Grid container spacing={4}>
      {/* <Grid item lg={12} md={12} sm={12} xs={12}>
        <div className="left-container">
          <h4>Banking Information</h4>
          <h6>See your Organisation Banking Information</h6>
        </div>
      </Grid> */}

      <Grid item lg={12} md={12} sm={12} xs={12}>
        <div className="right-container">
          <div className={classes.bankingheader}>
            <h4>Banking Information</h4>
            <h6>Manage your bank accounts</h6>
          </div>
          {bankAccounts.length > 0 ? (
            <div>
              {bankAccounts.map((bank, index) => (
                <Accordion
                  key={index}
                  expanded={expandedAccordion === index}
                  onChange={handleAccordionChange(index)}
                  sx={{
                    marginBottom: "10px",
                    boxShadow: "none",
                    padding: 0,
                    "&:before": { display: "none", padding: 0 }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Icon icon="ep:arrow-down-bold" width="24" height="24" />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    style={{ backgroundColor: "#F0F2F5", borderRadius: "8px" }}
                  >
                    <strong>Account Info {index + 1}</strong>
                  </AccordionSummary>

                  <AccordionDetails sx={{ padding: 0, marginTop: "10px" }}>
                    <div
                      className={`${classes.accord_body} ${bank.primary ? classes.accord_body_primary : ""}`}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: "1px solid #E4E7EC",
                        paddingBottom: "10px"
                      }}
                      >
                        <div style={{ display: "flex" }}>
                          <p className={classes.bank_currency}>
                            <strong>{getCurrencySymbol(bank.currency)}</strong>
                          </p>
                          <span><strong>{getCurrencyName(bank.currency)}</strong></span>
                        </div>

                        <Switch
                          checked={bank.status === "active"}
                          color="primary"
                          disabled
                        />
                      </div>

                      <Grid container spacing={2} sx={{ marginTop: "5px" }}>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Bank Name:</strong>
                            <span className={classes.span}>{bank.bank_name}</span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Account Number:</strong>
                            <span className={classes.span}>{bank.account_number}</span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Account Name:</strong>
                            <span className={classes.span}>{bank.account_name}</span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Sort Code:</strong>
                            <span className={classes.span}>{bank.sort_code || "N/A"}</span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          <p className={classes.account_details}>
                            <strong>Routing Number:</strong>
                            <span className={classes.span}>{bank.routing_number || "N/A"}</span>
                          </p>
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={12}>
                          {bank.primary && (
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              borderRadius: "12px"
                            }}
                            >
                              {[...Array(3)].map((_, starIndex) => (
                                <Icon
                                  key={starIndex}
                                  icon="tabler:star"
                                  width="20"
                                  height="20"
                                  className={classes.Icon}
                                />
                              ))}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </div>

                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderTop: "1px solid #eee",
                      paddingTop: "10px",
                      marginTop: "10px"
                    }}
                    >
                      <span
                        onClick={() => handleDeleteAccountModal(index)}
                        style={{
                          color: "red",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        <Icon
                          icon="material-symbols:delete-outline-rounded"
                          width="18"
                          style={{ marginRight: "5px" }}
                        />
                        Delete Account
                      </span>

                      <span
                        className={classes.Icon}
                        onClick={() => handleEditAccountModal(index)}
                        style={{ cursor: "pointer" }}
                      >
                        <Icon
                          icon="lucide:edit"
                          width="18"
                          style={{ marginRight: "5px" }}
                        />
                        Edit Account
                      </span>
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}

              <div
                className={classes.Icon}
                onClick={handleAddAccountModal}
                style={{ cursor: "pointer", marginTop: "20px" }}
              >
                <Icon icon="mdi:plus" width="20" height="20" />
                Add an Additional Account
              </div>
            </div>
          ) : (
            <div className={classes.empty}>
              <div>
                <NoData
                  img={NoAccount}
                  title="No Bank Added"
                  subtitle="There has been no bank account added for payments yet."
                />
                <span
                  className={classes.addIcon}
                  onClick={handleAddAccountModal}
                  style={{ cursor: "pointer" }}
                >
                  <Icon icon="mdi:plus" width="20" height="20" />
                Add an Account
                </span>
              </div>
            </div>
          )}
        </div>
      </Grid>

      {/* Add Account Modal */}
      <Modal
        title="Add Account Details"
        subtitle="Add a new account details"
        isopen={addAccountModal}
        closeModal={handleAddAccountModal}
        modalContent={
          <Grid container spacing={2} className="input-padding">
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <InputGroup
                label="Bank Name"
                required
                margin="normal"
                inputType="text"
                inputName="bank_name"
                value={bankDetails.bank_name}
                onChange={handleBankChange}
                placeholder="Enter bank name"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Account Number"
                required
                margin="normal"
                inputType="text"
                inputName="account_number"
                value={bankDetails.account_number}
                onChange={handleBankChange}
                placeholder="Enter account number"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Account Name"
                required
                margin="normal"
                inputType="text"
                inputName="account_name"
                value={bankDetails.account_name}
                onChange={handleBankChange}
                placeholder="Enter account name"
              />
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <div className="multiple-select">
                <p className="input-picker-title">
                  Currency<span className="input-required">*</span>
                </p>
                <Select
                  options={currencyOptions}
                  value={getSelectedCurrency()}
                  onChange={handleCurrencyChange}
                  placeholder="Select currency"
                  isSearchable
                />
              </div>
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Routing Number"
                margin="normal"
                inputType="text"
                inputName="routing_number"
                value={bankDetails.routing_number}
                onChange={handleBankChange}
                placeholder="Enter routing number"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Sort Code"
                margin="normal"
                inputType="text"
                inputName="sort_code"
                value={bankDetails.sort_code}
                onChange={handleBankChange}
                placeholder="Enter sort code"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bankDetails.status === "active"}
                    onChange={handleStatusChange}
                    name="status"
                    color="primary"
                  />
                }
                label="Active Account"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bankDetails.primary}
                    onChange={handleSwitchChange}
                    name="primary"
                    color="primary"
                  />
                }
                label="Primary Account"
              />
            </Grid>
          </Grid>
        }
        modalFooter={
          <div className={classes.modalFooterBtn}>
            <Button
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              buttonName="Cancel"
              onClick={handleAddAccountModal}
            />
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName="Add Account"
              onClick={() => handleBankAction("add")}
              isLoading={isLoading}
            />
          </div>
        }
      />

      {/* Edit Account Modal */}
      <Modal
        title="Edit Account Details"
        subtitle="Update your account details"
        isopen={editAccountModal}
        closeModal={() => handleEditAccountModal()}
        modalContent={
          <Grid container spacing={2} className="input-padding">
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <InputGroup
                label="Bank Name"
                required
                margin="normal"
                inputType="text"
                inputName="bank_name"
                value={bankDetails.bank_name}
                onChange={handleBankChange}
                placeholder="Enter bank name"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Account Number"
                required
                margin="normal"
                inputType="text"
                inputName="account_number"
                value={bankDetails.account_number}
                onChange={handleBankChange}
                placeholder="Enter account number"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Account Name"
                required
                margin="normal"
                inputType="text"
                inputName="account_name"
                value={bankDetails.account_name}
                onChange={handleBankChange}
                placeholder="Enter account name"
              />
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <div className="multiple-select">
                <p className="input-picker-title">
                  Currency<span className="input-required">*</span>
                </p>
                <Select
                  options={currencyOptions}
                  value={getSelectedCurrency()}
                  onChange={handleCurrencyChange}
                  placeholder="Select currency"
                  isSearchable
                />
              </div>
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Routing Number"
                margin="normal"
                inputType="text"
                inputName="routing_number"
                value={bankDetails.routing_number}
                onChange={handleBankChange}
                placeholder="Enter routing number"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Sort Code"
                margin="normal"
                inputType="text"
                inputName="sort_code"
                value={bankDetails.sort_code}
                onChange={handleBankChange}
                placeholder="Enter sort code"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bankDetails.status === "active"}
                    onChange={handleStatusChange}
                    name="status"
                    color="primary"
                  />
                }
                label="Active Account"
              />
            </Grid>

            <Grid item lg={6} md={6} sm={12} xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bankDetails.primary}
                    onChange={handleSwitchChange}
                    name="primary"
                    color="primary"
                  />
                }
                label="Primary Account"
              />
            </Grid>
          </Grid>
        }
        modalFooter={
          <div className={classes.modalFooterBtn}>
            <Button
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              buttonName="Cancel"
              onClick={() => handleEditAccountModal()}
            />
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName="Update Account"
              onClick={() => handleBankAction("edit")}
              isLoading={isLoading}
            />
          </div>
        }
      />

      {/* Delete Account Modal */}
      <Modal
        title="Delete Account"
        subtitle="Are you sure you want to delete this bank account?"
        isopen={deleteAccountModal}
        closeModal={handleDeleteAccountModal}
        modalContent={
          <div className="input-padding">
            <p>This action cannot be undone. All associated data with this bank account will be permanently removed.</p>
            {selectedBankIndex !== null && bankAccounts[selectedBankIndex] && (
              <div style={{
                marginTop: "15px",
                padding: "10px",
                backgroundColor: "#f8f9fa",
                borderRadius: "5px"
              }}
              >
                <p><strong>Bank:</strong> {bankAccounts[selectedBankIndex].bank_name}</p>
                <p><strong>Account Number:</strong> {bankAccounts[selectedBankIndex].account_number}</p>
                <p><strong>Account Name:</strong> {bankAccounts[selectedBankIndex].account_name}</p>
                <p><strong>Currency:</strong> {bankAccounts[selectedBankIndex].currency || "N/A"}</p>
              </div>
            )}
          </div>
        }
        modalFooter={
          <div className={classes.modalFooterBtn}>
            <Button
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              buttonName="Cancel"
              onClick={handleDeleteAccountModal}
            />
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName="Delete Account"
              onClick={() => handleBankAction("delete")}
              isLoading={isLoading}
            />
          </div>
        }
      />

      <Modal
        title="Verify OTP"
        subtitle="Please enter the OTP sent to your Email"
        isopen={otpModal}
        closeModal={handleOtpModal}
        modalContent={
          <Grid container spacing={2} className="input-padding">
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <InputGroup
                label="OTP"
                required
                margin="normal"
                inputType="text"
                inputName="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <div style={{ textAlign: "center", marginTop: "10px" }}>
                <span
                  onClick={resendOtp}
                  className={classes.Icon}
                >
                  Resend OTP
                  <Icon icon="material-symbols-light:send-rounded" width="24" height="24" />
                </span>
              </div>
            </Grid>
          </Grid>
        }
        modalFooter={
          <div className={classes.modalFooterBtn}>
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName="Verify Otp"
              onClick={verifyOtp}
              isLoading={isOtpLoading}
            />
          </div>
        }
      />
      {/* <ToastContainer /> */}
    </Grid>
  );
};

export default BankingInfo;
